"use client";

import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { NoAccess } from "@saas/account/ui/no-access/no-access";
import { IBranchTypeEnum } from "@saas/account/utils/@enum/org/branch-type/branch-type.enum";
import { AccessesKeyEnum } from "@saas/account/utils/@enum/subaccount/accesses-key/accesses-key.enum";
import { NavigationInterface } from "@saas/layout/utils/@interface/navigation/navigation.interface";
import { useLayoutShowAside } from "@saas/layout/utils/^hook/use-layout-show-aside/use-layout-show-aside";
import { DesktopFeatureBlockerModal } from "@saas/shared/ui/desktop-feature-blocker-modal/desktop-feature-blocker-modal";
import { OfflineState } from "@saas/shared/ui/offline-state/offline-state";
import { ProgressBar } from "@saas/shared/ui/progress-bar/progress-bar";
import { SlideOver } from "@saas/shared/ui/slide-over/slide-over";
import { AppNameEnum } from "@saas/shared/utils/@enum/app-name/app-name.enum";
import { PathKeysEnum } from "@saas/shared/utils/@enum/path-keys/path-keys.enum";
import { useProgress } from "@saas/shared/utils/^hook/use-progress/use-progress";
import { useNewIndicator } from "@saas/shared/utils/hook/new-indicator/use-new-indicator";

import { AsideNavigation } from "../aside-navigation/aside-navigation";
import { Snackbar } from "../snackbar/snackbar";
import { TopNavigationBar } from "../top-navigation-bar/top-navigation-bar";

import { useNetworkState } from "react-use";

export interface AppLayoutProps {
  children: ReactNode;
  enabledNotificationR2?: boolean;
  hasAccess: boolean;
  hasParentId: string | null;
  navigation: ReadonlyArray<NavigationInterface>;
  statusCode: number;
  accesses?: AccessesKeyEnum[];
  isVerified?: boolean;
  variantLogo?: AppNameEnum;
  accountMenu: () => JSX.Element;
  unverifiedAccountBlockerModal: () => JSX.Element;

  // New props for organization
  organizationName?: string;
  branchName?: string;
  branchType?: IBranchTypeEnum;
}

// TODO: move this to feature folder
export const AppLayout = ({
  children,
  enabledNotificationR2,
  hasAccess,
  hasParentId,
  navigation,
  statusCode,
  accesses,
  isVerified,
  variantLogo = AppNameEnum.MELAKA,
  accountMenu,
  unverifiedAccountBlockerModal: UnverifiedAccountBlockerModal,
  organizationName,
  branchName,
  branchType,
}: AppLayoutProps) => {
  const desktopFeatureBlockerModalRef = useRef<HTMLButtonElement>(null);
  const [isOnline, setIsOnline] = useState<boolean | undefined>(true);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);

  const progressBarProps = useProgress();
  const { online } = useNetworkState();

  const { setShowAside, showAside } = useLayoutShowAside();

  const isParentIdExist = hasParentId !== null;
  const isAccessesExist = accesses && accesses?.length > 0;
  const isSubaccount = isAccessesExist && isParentIdExist;

  const hasPermissions = hasAccess || !isSubaccount;

  const excludedPaths = [PathKeysEnum.INVENTORY];
  const newIndicatorPaths = useMemo(
    () =>
      navigation.reduce<ReadonlyArray<string>>(
        (acc, { href, isNew, children }) => {
          if (children) {
            return children.reduce((acc, { href, isNew: isNewChildren }) => {
              return isNewChildren ? [...acc, href] : acc;
            }, acc);
          }

          if (isNew) return [...acc, href];

          return acc;
        },
        []
      ),
    [navigation]
  );

  const { showIndicator, onHideIndicator: handleHideIndicator } =
    useNewIndicator(newIndicatorPaths);

  useEffect(() => {
    setIsOnline(online);
    if (statusCode === 404) {
      setIsNotFound(true);
    }
  }, [online, statusCode]);

  return (
    <div className={"bg-neutral-N100 flex min-h-screen flex-col"}>
      <div className={"hidden md:block"}>
        <AsideNavigation
          navigation={navigation}
          accesses={accesses}
          showIndicator={showIndicator}
          onNavigate={(route) => {
            route && handleHideIndicator(route);
          }}
        />
      </div>
      <div className={"block md:hidden"}>
        <SlideOver
          origin={"left"}
          size={"small"}
          open={showAside}
          onClose={() => setShowAside(false)}
        >
          <AsideNavigation
            navigation={navigation}
            accesses={accesses}
            showIndicator={showIndicator}
            onNavigate={(route) => {
              setShowAside(false);

              route && handleHideIndicator(route);
            }}
          />
        </SlideOver>
      </div>

      <div className={"flex flex-1 flex-col md:pl-[248px]"}>
        <TopNavigationBar
          accountMenu={accountMenu}
          handleShowAside={() => setShowAside(true)}
          enabledNotificationR2={enabledNotificationR2}
          variantLogo={variantLogo}
          showIndicator={showIndicator}
          organizationName={organizationName}
          branchName={branchName}
          branchType={branchType}
        />

        <ProgressBar
          {...progressBarProps}
          key={progressBarProps.key}
          className={"top-[58px] left-auto right-0 md:w-[calc(100%_-_248px)]"}
        />

        <main className={"relative flex min-h-full w-full flex-1 flex-col"}>
          <div>
            {!hasPermissions && !isNotFound ? (
              <NoAccess />
            ) : (
              <>
                <Snackbar className={"pt-[58px] md:pl-[248px]"} />

                <UnverifiedAccountBlockerModal />

                <DesktopFeatureBlockerModal
                  initialRef={desktopFeatureBlockerModalRef}
                  isVerified={isVerified}
                  excludedPaths={excludedPaths}
                />

                {isOnline ? (
                  <div>{children}</div>
                ) : (
                  <div className={"mt-12 p-10 md:mt-24"}>
                    <OfflineState />
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
